import React, { memo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { useTranslation } from 'react-i18next';
import {
  Users,
  UserPlus,
  GitBranch,
  Tag,
  FileText,
  Target,
  Phone,
  MessageSquare,
  Mail,
  Link2,
} from 'lucide-react';

interface CRMActionNodeData {
  label: string;
  type: string;
  icon?: string;
  config: Record<string, any>;
  isConfigured?: boolean;
}

const iconMap: Record<string, React.ReactNode> = {
  UserPlus: <UserPlus className="w-5 h-5" />,
  Users: <Users className="w-5 h-5" />,
  GitBranch: <GitBranch className="w-5 h-5" />,
  Tag: <Tag className="w-5 h-5" />,
  FileText: <FileText className="w-5 h-5" />,
  Target: <Target className="w-5 h-5" />,
  Phone: <Phone className="w-5 h-5" />,
  MessageSquare: <MessageSquare className="w-5 h-5" />,
  Mail: <Mail className="w-5 h-5" />,
};

const CRMActionNode: React.FC<NodeProps<CRMActionNodeData>> = ({ data, selected }) => {
  const { t } = useTranslation();

  const getConfigPreview = () => {
    if (data.type === 'create_lead' && data.config?.pipelineId) {
      return `Pipeline: ${data.config.pipelineId}`;
    }
    if (data.type === 'change_stage' && data.config?.targetStageId) {
      return `${t('workflow.to', 'Para')}: ${data.config.targetStageId}`;
    }
    if (data.type === 'add_tag' && data.config?.tagName) {
      return `Tag: ${data.config.tagName}`;
    }
    if (data.type === 'create_ticket' && data.config?.ticketTitle) {
      return `${t('workflow.ticket', 'Ticket')}: ${data.config.ticketTitle.substring(0, 25)}...`;
    }
    if ((data.type === 'crm_whatsapp' || data.type === 'send_whatsapp') && data.config?.templateName) {
      return `Template: ${data.config.templateName}`;
    }
    if ((data.type === 'crm_whatsapp' || data.type === 'send_whatsapp') && data.config?.whatsappMode === 'freetext' && data.config?.message) {
      return data.config.message.substring(0, 30) + (data.config.message.length > 30 ? '...' : '');
    }
    // E-mail tem preview próprio (assunto + trecho do texto) logo abaixo.
    if (data.type === 'crm_email' || data.type === 'send_email') return null;
    if ((data.type === 'crm_sms' || data.type === 'send_sms') && data.config?.message) {
      return data.config.message.substring(0, 30) + (data.config.message.length > 30 ? '...' : '');
    }
    if (data.type === 'update_lead' && (data.config?.name || data.config?.email)) {
      return data.config.name || data.config.email;
    }
    if (data.type === 'assign_owner' && data.config?.ownerId) {
      return `${t('workflow.owner', 'Responsavel')}: ${data.config.ownerId}`;
    }
    if ((data.type === 'crm_call' || data.type === 'schedule_call') && data.config?.scheduledAt) {
      return `${t('workflow.scheduled', 'Agendado')}`;
    }
    return null;
  };

  const configPreview = getConfigPreview();

  // Preview do e-mail direto no bloco: assunto + começo do texto.
  // Sem isso o cliente não conseguia conferir o e-mail que a IA escreveu sem
  // abrir cada bloco ("a IA é muito legal, mas não consigo verificar depois").
  const emailPreview = data.type === 'crm_email' || data.type === 'send_email'
    ? {
        subject: String(data.config?.subject || '').trim(),
        body: String(data.config?.body || '')
          .replace(/<br\s*\/?>/gi, ' ')
          .replace(/<[^>]+>/g, ' ')
          .replace(/\s+/g, ' ')
          .trim(),
      }
    : null;

  return (
    <div
      className={`relative min-w-[180px] bg-white dark:bg-gray-800 rounded-xl shadow-lg border-2 transition-all ${
        selected
          ? 'border-pink-500 shadow-pink-100 dark:shadow-pink-900/30'
          : 'border-pink-200 dark:border-pink-800 hover:border-pink-400'
      }`}
    >
      {/* Handle - Input */}
      <Handle
        type="target"
        position={Position.Top}
        className="!w-4 !h-4 !bg-pink-500 !border-2 !border-white dark:!border-gray-800 !rounded-full"
      />

      {/* Header */}
      <div className="bg-gradient-to-r from-pink-500 to-rose-500 rounded-t-lg px-4 py-2">
        <div className="flex items-center gap-2 text-white">
          <Link2 className="w-4 h-4" />
          <span className="text-xs font-medium uppercase tracking-wide">CRM</span>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-pink-100 dark:bg-pink-900/30 rounded-lg flex items-center justify-center text-pink-600 dark:text-pink-400">
            {iconMap[data.icon || 'Users'] || <Users className="w-5 h-5" />}
          </div>
          <div className="flex-1">
            <p className="font-medium text-gray-900 dark:text-white text-sm leading-tight">
              {data.label}
            </p>
            {data.isConfigured ? (
              <span className="text-xs text-pink-600 dark:text-pink-400">{t('workflow.configured', 'Configurado')}</span>
            ) : (
              <span className="text-xs text-yellow-600 dark:text-yellow-400">{t('workflow.clickToConfigure', 'Clique para configurar')}</span>
            )}
          </div>
        </div>

        {/* Preview do e-mail (assunto + trecho do corpo) */}
        {emailPreview && (emailPreview.subject || emailPreview.body) && (
          <div
            data-testid="email-node-preview"
            className="mt-3 p-2.5 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700 max-w-[240px]"
          >
            {emailPreview.subject && (
              <p className="text-[11px] font-semibold text-gray-700 dark:text-gray-200 leading-snug">
                ✉️ {emailPreview.subject}
              </p>
            )}
            {emailPreview.body && (
              <p
                className="mt-1 text-[11px] text-gray-500 dark:text-gray-400 leading-snug overflow-hidden"
                style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}
              >
                {emailPreview.body}
              </p>
            )}
            <p className="mt-1.5 text-[10px] text-pink-600 dark:text-pink-400">
              {t('workflow.email.clickToRead', 'Clique no bloco para ler/editar o e-mail completo')}
            </p>
          </div>
        )}
        {emailPreview && !emailPreview.subject && !emailPreview.body && (
          <div className="mt-3 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
            <p className="text-[11px] text-yellow-700 dark:text-yellow-400">
              {t('workflow.email.empty', 'E-mail ainda sem conteúdo — clique para escrever')}
            </p>
          </div>
        )}

        {/* Config Preview */}
        {configPreview && (
          <div className="mt-3 p-2 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {configPreview}
            </p>
          </div>
        )}

        {/* CRM Badge */}
        <div className="mt-2 flex items-center justify-end">
          <span className="text-[10px] bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400 px-2 py-0.5 rounded-full font-medium">
            DevSkin CRM
          </span>
        </div>
      </div>

      {/* Handle - Output */}
      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-4 !h-4 !bg-pink-500 !border-2 !border-white dark:!border-gray-800 !rounded-full"
      />
    </div>
  );
};

export default memo(CRMActionNode);
